/**
 * analyze.js — 断卦规则引擎
 *
 * 这里只产出**可验证的结构化事实**：某爻旺衰如何、被谁生、被谁克、
 * 空不空、破不破、动化什么。它不产出「你会发财」这类结论。
 *
 * 断语的最后一步（把这些事实串成人话）交给上层——你可以自己写规则，
 * 也可以把 facts 丢给 LLM 让它组织语言。但**判断依据必须来自这里**，
 * 不能让模型自己去编卦象，否则就又变成空有外表。
 */

import { BRANCHES, BRANCH_ELEMENT } from './ganzhi.js';
import {
  buildHexagram, hiddenSpirits, sixGods, sixRelative, generates, overcomes
} from './hexagram.js';
import {
  LINE_STATE_WEIGHT_IDS, MONTH_BREAK_WEIGHT_IDS, weightValue
} from './verdict-weights.js';

/* ------------------------------------------------------ 地支关系 */

const idx = (b) => BRANCHES.indexOf(b);

/** 六冲：相隔六位 */
export const clashes = (a, b) => (idx(a) - idx(b) + 12) % 12 === 6;

/** 六合 */
export const SIX_COMBINE = {
  子: '丑', 丑: '子', 寅: '亥', 亥: '寅', 卯: '戌', 戌: '卯',
  辰: '酉', 酉: '辰', 巳: '申', 申: '巳', 午: '未', 未: '午'
};
export const combines = (a, b) => SIX_COMBINE[a] === b;

/** 内外卦三对爻（初四、二五、三上）全冲，判为六冲卦。 */
export function isSixClashHexagram(hexagram) {
  return Boolean(hexagram?.yao?.length === 6
    && [[0, 3], [1, 4], [2, 5]].every(([a, b]) => (
      clashes(hexagram.yao[a].branch, hexagram.yao[b].branch)
    )));
}

/** 内外卦三对爻（初四、二五、三上）全合，判为六合卦。 */
export function isSixCombineHexagram(hexagram) {
  return Boolean(hexagram?.yao?.length === 6
    && [[0, 3], [1, 4], [2, 5]].every(([a, b]) => (
      combines(hexagram.yao[a].branch, hexagram.yao[b].branch)
    )));
}

/** 三合局 */
export const TRIPLE_COMBINE = [
  { branches: ['申', '子', '辰'], element: '水' },
  { branches: ['亥', '卯', '未'], element: '木' },
  { branches: ['寅', '午', '戌'], element: '火' },
  { branches: ['巳', '酉', '丑'], element: '金' }
];

/** 六害 */
export const SIX_HARM = {
  子: '未', 未: '子', 丑: '午', 午: '丑', 寅: '巳', 巳: '寅',
  卯: '辰', 辰: '卯', 申: '亥', 亥: '申', 酉: '戌', 戌: '酉'
};

/** 三刑与自刑 */
export function punishes(a, b) {
  const groups = [['寅', '巳', '申'], ['丑', '戌', '未']];
  for (const g of groups) if (g.includes(a) && g.includes(b) && a !== b) return true;
  if ((a === '子' && b === '卯') || (a === '卯' && b === '子')) return true;
  if (a === b && ['辰', '午', '酉', '亥'].includes(a)) return true;
  return false;
}

/** 月建、日辰、爻支恰好凑齐寅巳申或丑戌未，谓三刑全见。 */
export function fullPunishGroup(monthBranch, dayBranch, lineBranch) {
  const actual = new Set([monthBranch, dayBranch, lineBranch]);
  if (actual.size !== 3) return null;
  const groups = [
    { branches: ['寅', '巳', '申'], name: '寅巳申' },
    { branches: ['丑', '戌', '未'], name: '丑戌未' }
  ];
  return groups.find(({ branches }) => branches.every((branch) => actual.has(branch))) || null;
}

/* --------------------------------------------------- 十二长生 */

/** 各五行的长生位（水土同宫，从申起） */
const CHANGSHENG_START = { 金: '巳', 木: '亥', 水: '申', 土: '申', 火: '寅' };
const CHANGSHENG_ORDER = [
  '长生', '沐浴', '冠带', '临官', '帝旺', '衰',
  '病', '死', '墓', '绝', '胎', '养'
];

/** 某五行在某地支上的十二长生状态 */
export function changSheng(element, branch) {
  const start = idx(CHANGSHENG_START[element]);
  return CHANGSHENG_ORDER[(idx(branch) - start + 12) % 12];
}

export const tombOf = (el) => ({ 金: '丑', 木: '未', 水: '辰', 土: '辰', 火: '戌' }[el]);
export const graveOf = (el) => ({ 金: '寅', 木: '申', 水: '巳', 土: '巳', 火: '亥' }[el]);

/* ------------------------------------------------- 旺相休囚死 */

const SEASON_ELEMENT = {
  寅: '木', 卯: '木', 巳: '火', 午: '火', 申: '金', 酉: '金',
  亥: '水', 子: '水', 辰: '土', 戌: '土', 丑: '土', 未: '土'
};

/** 以月令五行判断某五行的旺相休囚死 */
export function seasonalState(element, monthBranch) {
  const m = SEASON_ELEMENT[monthBranch];
  if (element === m) return '旺';
  if (generates(m, element)) return '相'; // 月令所生
  if (generates(element, m)) return '休'; // 生月令者
  if (overcomes(element, m)) return '囚'; // 克月令者
  if (overcomes(m, element)) return '死'; // 被月令克
  return '平';
}

const STATE_PRIORITY = { 旺: 5, 相: 4, 休: 3, 囚: 2, 死: 1, 平: 0 };

/** 与 verdict() 的取主爻顺序一致；只为输出结构化主用神证据。 */
function primaryUseLine(lines) {
  return [...lines].sort((a, b) => {
    if (a.isShi !== b.isShi) return Number(b.isShi) - Number(a.isShi);
    if (a.moving !== b.moving) return Number(b.moving) - Number(a.moving);
    if (STATE_PRIORITY[a.state] !== STATE_PRIORITY[b.state]) {
      return STATE_PRIORITY[b.state] - STATE_PRIORITY[a.state];
    }
    const sound = (line) => !line.marks.some((mark) => ['旬空', '月破', '日破'].includes(mark));
    if (sound(a) !== sound(b)) return Number(sound(b)) - Number(sound(a));
    return a.position - b.position;
  })[0] || null;
}

function evidenceOf(line) {
  if (!line) return null;
  return {
    relative: line.relative,
    branch: line.branch,
    position: line.position,
    ganzhi: line.ganzhi,
    element: line.element
  };
}

function calendarEffects(line, source, actorBranch) {
  const effects = [];
  const actorElement = BRANCH_ELEMENT[actorBranch];
  if (generates(actorElement, line.element)) effects.push('生');
  if (overcomes(actorElement, line.element)) effects.push('克');
  if (clashes(actorBranch, line.branch)) effects.push('冲');
  if (combines(actorBranch, line.branch)) effects.push('合');
  if (punishes(actorBranch, line.branch)) effects.push('刑');
  if (SIX_HARM[actorBranch] === line.branch) effects.push('害');
  return effects.map((type) => ({ source, targetPosition: line.position, type }));
}

function actorEvidence(line) {
  return {
    source: line.moving ? '动爻' : line.marks?.includes('暗动') ? '暗动爻' : '静爻',
    position: line.position,
    relative: line.relative,
    branch: line.branch,
    ganzhi: line.ganzhi,
    element: line.element
  };
}

function uniqueActors(actors) {
  const seen = new Set();
  return actors.filter((actor) => {
    const key = `${actor.source}:${actor.position ?? ''}:${actor.branch}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** 只聚合已有生克冲合事实，不参与 score 或 verdict 加权。 */
function compoundFacts({ yao, interactions, calendarSupport, pillars, useGod }) {
  const calendarActor = (support) => support.source === '日辰'
    ? { source: '日辰', branch: pillars.day.branch, ganzhi: pillars.day.name }
    : { source: '月建', branch: pillars.month.branch, ganzhi: pillars.month.name };
  const facts = [];

  for (const target of yao) {
    const incoming = (type) => uniqueActors([
      ...calendarSupport
        .filter((support) => support.targetPosition === target.position && support.type === type)
        .map(calendarActor),
      ...interactions
        .filter((interaction) => interaction.to === target.position && interaction.type === type)
        .map((interaction) => actorEvidence(yao[interaction.from - 1]))
    ]);
    const overcomeBy = incoming('克');
    const generatedBy = incoming('生');
    if (overcomeBy.length && generatedBy.length) {
      facts.push({
        type: '克处逢生',
        target: evidenceOf(target),
        overcomeBy,
        generatedBy
      });
    }

    // 月建冲爻即为月破；是否“到底而破”由 monthBreakSeverity 另行表达。
    if (clashes(target.branch, pillars.month.branch)) {
      const currentCalendar = calendarSupport
        .filter((support) => support.targetPosition === target.position && support.type === '合')
        .map(calendarActor);
      const activeLines = yao
        .filter((line) => line.position !== target.position
          && (line.moving || line.marks.includes('暗动'))
          && combines(line.branch, target.branch))
        .map(actorEvidence);
      facts.push({
        type: '破而逢合',
        target: evidenceOf(target),
        brokenBy: { source: '月建', branch: pillars.month.branch, ganzhi: pillars.month.name },
        currentClassification: '月破',
        monthBreakSeverity: target.monthBreakSeverity,
        combineBranch: SIX_COMBINE[target.branch],
        combinedBy: uniqueActors([...currentCalendar, ...activeLines])
      });
    }
  }

  // 元神动生用神，但用神同时月破或受日辰所克：只聚合可核事实，
  // 不据此判断吉凶，也不归纳“用神无根”的通用判据。
  const useTargets = useGod?.主爻?.length
    ? useGod.主爻
    : useGod?.伏神 ? [useGod.伏神] : [];
  for (const target of useTargets) {
    const originalSpirits = yao
      .filter((line) => line.moving && generates(line.element, target.element))
      .map(actorEvidence);
    const isDayOvercome = overcomes(BRANCH_ELEMENT[pillars.day.branch], target.element);
    if (originalSpirits.length && (target.isMonthBreak || isDayOvercome)) {
      facts.push({
        type: '元神发动而用神无气',
        target: evidenceOf(target),
        originalSpirits: uniqueActors(originalSpirits),
        conditions: {
          isMonthBreak: Boolean(target.isMonthBreak),
          month: target.isMonthBreak
            ? { source: '月建', branch: pillars.month.branch, ganzhi: pillars.month.name }
            : null,
          isDayOvercome,
          day: isDayOvercome
            ? { source: '日辰', branch: pillars.day.branch, ganzhi: pillars.day.name }
            : null
        }
      });
    }
  }
  return facts;
}

/** 已成的多个三合局之间的有向五行边；只输出关系。 */
function tripleCombineRelations(patterns) {
  const active = TRIPLE_COMBINE.filter(({ element }) => patterns.includes(`三合${element}局`));
  const edges = [];
  for (let i = 0; i < active.length; i += 1) {
    for (let j = i + 1; j < active.length; j += 1) {
      const a = active[i];
      const b = active[j];
      if (generates(a.element, b.element)) {
        edges.push({ from: `三合${a.element}局`, to: `三合${b.element}局`, type: '生' });
      } else if (generates(b.element, a.element)) {
        edges.push({ from: `三合${b.element}局`, to: `三合${a.element}局`, type: '生' });
      } else if (overcomes(a.element, b.element)) {
        edges.push({ from: `三合${a.element}局`, to: `三合${b.element}局`, type: '克' });
      } else if (overcomes(b.element, a.element)) {
        edges.push({ from: `三合${b.element}局`, to: `三合${a.element}局`, type: '克' });
      }
    }
  }
  return edges;
}

const supportsElement = (sourceElement, targetElement) => (
  sourceElement === targetElement || generates(sourceElement, targetElement)
);

/**
 * 月破的效力分层。月建冲即为月破；只有静爻且全无日辰、动爻生助时，
 * 才按原文判作“到底而破”。本函数只组合已知五行、动静事实。
 */
function classifyMonthBreak({ isMonthBreak, isMoving, element, dayBranch, movingElements }) {
  if (!isMonthBreak) return null;
  const hasDaySupport = supportsElement(BRANCH_ELEMENT[dayBranch], element);
  const hasMovingSupport = movingElements.some((sourceElement) => (
    supportsElement(sourceElement, element)
  ));
  return !isMoving && !hasDaySupport && !hasMovingSupport ? '到底而破' : '可解';
}

/* ---------------------------------------------- 进神 / 退神 */

const ADVANCE = { 寅: '卯', 巳: '午', 申: '酉', 亥: '子', 丑: '辰', 辰: '未', 未: '戌', 戌: '丑' };

function transformKind(from, to) {
  if (from === to) return '伏吟';
  if (clashes(from, to)) return '反吟';
  if (ADVANCE[from] === to) return '化进神';
  if (ADVANCE[to] === from) return '化退神';
  const a = BRANCH_ELEMENT[from];
  const b = BRANCH_ELEMENT[to];
  if (generates(b, a)) return '回头生';
  if (overcomes(b, a)) return '回头克';
  if (generates(a, b)) return '化泄';
  if (overcomes(a, b)) return '化耗';
  return '化比和';
}

/* ------------------------------------------------------- 用神 */

/** 常见问事分类 → 用神六亲。可用 useRelative 直接覆盖。 */
export const USE_GOD_MAP = {
  求财: '妻财', 生意: '妻财', 投资: '妻财', 借贷: '妻财', 薪资: '妻财', 失物: '妻财',
  事业: '官鬼', 工作: '官鬼', 升迁: '官鬼', 考公: '官鬼', 官司: '官鬼', 疾病: '官鬼',
  学业: '父母', 考试: '父母', 文书: '父母', 合同: '父母', 房产: '父母', 车辆: '父母',
  父母: '父母', 长辈: '父母', 天气: '父母',
  子女: '子孙', 晚辈: '子孙', 宠物: '子孙', 求医: '子孙', 出行: '子孙', 解忧: '子孙',
  兄弟: '兄弟', 朋友: '兄弟', 同事: '兄弟', 合伙: '兄弟', 竞争: '兄弟',
  婚姻男测: '妻财', 婚姻女测: '官鬼',
  自身: '世爻', 运势: '世爻'
};

/** 用神→原神(生用神)/忌神(克用神)/仇神(生忌神) */
function relativeChain(useRelative, palaceElement) {
  const REL = ['父母', '兄弟', '子孙', '妻财', '官鬼'];
  const elOf = {
    父母: null, 兄弟: null, 子孙: null, 妻财: null, 官鬼: null
  };
  const ELS = ['木', '火', '土', '金', '水'];
  for (const el of ELS) {
    const r = {
      木: null
    };
    void r;
  }
  // 由五行反推：以宫五行为我
  const me = palaceElement;
  const SHENG = { 木: '火', 火: '土', 土: '金', 金: '水', 水: '木' };
  const KE = { 木: '土', 土: '水', 水: '火', 火: '金', 金: '木' };
  const inv = (map, v) => Object.keys(map).find((k) => map[k] === v);
  elOf.兄弟 = me;
  elOf.子孙 = SHENG[me];
  elOf.妻财 = KE[me];
  elOf.父母 = inv(SHENG, me);
  elOf.官鬼 = inv(KE, me);
  void REL;

  const useEl = elOf[useRelative];
  const yuan = inv(SHENG, useEl); // 生用神者
  const ji = inv(KE, useEl); // 克用神者
  const chou = inv(SHENG, ji); // 生忌神者
  const nameOf = (el) => Object.keys(elOf).find((k) => elOf[k] === el);
  return {
    用神: useRelative,
    用神五行: useEl,
    原神: nameOf(yuan),
    忌神: nameOf(ji),
    仇神: nameOf(chou)
  };
}

/* ------------------------------------------------------ 主入口 */

/**
 * 完整排盘 + 事实提取。
 *
 * @param {object} p
 *   values      六个爻值 6/7/8/9（自下而上），来自 cast()
 *   pillars     fourPillars() 的结果
 *   question    问题原文（可选）
 *   category    问事分类，见 USE_GOD_MAP（可选）
 *   useRelative 直接指定用神六亲（优先于 category）
 *   timeHorizon 'near' | 'far'（可选）；只在原问题明确远近时传入，不从类别猜测
 *   timeScope   'none' | 'near' | 'mid' | 'long'（可选）；控制报告展示的日期范围
 */
export function analyze(p) {
  const {
    values, pillars, question = '', category, useRelative,
    timeHorizon = null, timeScope = null
  } = p;
  if (timeHorizon !== null && timeHorizon !== 'near' && timeHorizon !== 'far') {
    throw new TypeError("timeHorizon 只能是 'near'、'far' 或 null");
  }
  if (timeScope !== null && !['none', 'near', 'mid', 'long'].includes(timeScope)) {
    throw new TypeError("timeScope 只能是 'none'、'near'、'mid'、'long' 或 null");
  }
  const lines = values.map((v) => (v === 7 || v === 9 ? 1 : 0));
  const changedLines = values.map((v, i) => (v === 9 ? 0 : v === 6 ? 1 : lines[i]));
  const moving = values.map((v, i) => (v === 6 || v === 9 ? i + 1 : 0)).filter(Boolean);

  const primary = buildHexagram(lines);
  const hasChange = moving.length > 0;
  const changed = hasChange ? buildHexagram(changedLines) : null;
  const rawHidden = hiddenSpirits(primary);
  const gods = sixGods(pillars.day.stem);

  const monthBranch = pillars.month.branch;
  const dayBranch = pillars.day.branch;
  const voids = pillars.voidBranches;
  const movingElements = moving.map((position) => primary.yao[position - 1].element);

  const hidden = rawHidden.map((spirit) => {
    const state = seasonalState(spirit.element, monthBranch);
    const strong = state === '旺' || state === '相' || spirit.branch === dayBranch
      || generates(BRANCH_ELEMENT[dayBranch], spirit.element);
    const changShengStage = changSheng(spirit.element, dayBranch);
    const isMonthBreak = clashes(spirit.branch, monthBranch);
    return {
      ...spirit,
      state,
      changShengStage,
      isVoid: voids.includes(spirit.branch),
      isMonthBreak,
      monthBreakSeverity: classifyMonthBreak({
        isMonthBreak,
        isMoving: false,
        element: spirit.element,
        dayBranch,
        movingElements
      }),
      isDayBreak: clashes(spirit.branch, dayBranch) && !strong,
      isTomb: changShengStage === '墓',
      isGrave: changShengStage === '绝',
      isAbsent: true
    };
  });

  // ---- 逐爻建档 ----
  const yao = primary.yao.map((y, i) => {
    const state = seasonalState(y.element, monthBranch);
    const isMoving = moving.includes(i + 1);
    const isVoid = voids.includes(y.branch);
    const changShengStage = changSheng(y.element, dayBranch);
    const monthClash = clashes(y.branch, monthBranch);
    const dayClash = clashes(y.branch, dayBranch);
    const strong = state === '旺' || state === '相' || y.branch === dayBranch
      || generates(BRANCH_ELEMENT[dayBranch], y.element);

    const marks = [];
    if (y.branch === monthBranch) marks.push('临月建');
    if (y.branch === dayBranch) marks.push('临日辰');
    if (monthClash) marks.push('月破');
    if (dayClash) marks.push(isMoving ? '动而逢冲(散)' : strong ? '暗动' : '日破');
    if (combines(y.branch, dayBranch)) marks.push('日合');
    if (combines(y.branch, monthBranch)) marks.push('月合');
    if (SIX_HARM[y.branch] === dayBranch) marks.push('日害');
    const dayPunish = punishes(y.branch, dayBranch);
    const monthPunish = punishes(y.branch, monthBranch);
    const fullPunish = fullPunishGroup(monthBranch, dayBranch, y.branch);
    if (dayPunish) marks.push('日刑');
    if (monthPunish) marks.push('月刑');
    if (fullPunish) marks.push('三刑全见');
    if (isVoid) marks.push(isMoving || strong ? '空而不空' : '旬空');
    if (generates(BRANCH_ELEMENT[dayBranch], y.element)) marks.push('日生');
    if (overcomes(BRANCH_ELEMENT[dayBranch], y.element)) marks.push('日克');
    if (generates(BRANCH_ELEMENT[monthBranch], y.element)) marks.push('月生');
    if (overcomes(BRANCH_ELEMENT[monthBranch], y.element)) marks.push('月克');
    // 十二长生全部作为可验证事实；现有加权仍只识别日墓/日绝。
    marks.push(changShengStage);
    if (changShengStage === '墓') marks.push('日墓');
    if (changShengStage === '绝') marks.push('日绝');

    let transform = null;
    if (isMoving && changed) {
      const to = changed.yao[i];
      transform = {
        to: to.ganzhi,
        /** 供应期等上层直接查表，避免从干支字符串反切地支。 */
        toBranch: to.branch,
        toElement: to.element,
        // 变爻只借变卦的纳甲干支；六亲始终以本卦宫五行为“我”重算。
        toRelative: sixRelative(primary.palaceElement, to.element),
        kind: transformKind(y.branch, to.branch),
        toVoid: voids.includes(to.branch),
        toState: seasonalState(to.element, monthBranch)
      };
      marks.push(transform.kind);
      if (transform.toVoid) marks.push('化空');
      if (changSheng(y.element, to.branch) === '墓') marks.push('化墓');
      if (changSheng(y.element, to.branch) === '绝') marks.push('化绝');
    }

    const monthBreakSeverity = classifyMonthBreak({
      isMonthBreak: monthClash,
      isMoving,
      element: y.element,
      dayBranch,
      movingElements
    });

    const scoreWeightIds = [LINE_STATE_WEIGHT_IDS[state] || LINE_STATE_WEIGHT_IDS.平];
    if (y.branch === dayBranch) scoreWeightIds.push('day-same-branch');
    else if (generates(BRANCH_ELEMENT[dayBranch], y.element)) scoreWeightIds.push('day-generates-line');
    else if (overcomes(BRANCH_ELEMENT[dayBranch], y.element)) scoreWeightIds.push('day-overcomes-line');
    if (monthBreakSeverity) scoreWeightIds.push(MONTH_BREAK_WEIGHT_IDS[monthBreakSeverity]);
    if (marks.includes('日破')) scoreWeightIds.push('day-break');
    if (marks.includes('旬空')) scoreWeightIds.push('void');
    if (transform && transform.kind === '回头生') scoreWeightIds.push('transform-return-generate');
    if (transform && transform.kind === '回头克') scoreWeightIds.push('transform-return-overcome');
    if (transform && transform.kind === '化进神') scoreWeightIds.push('transform-advance');
    if (transform && transform.kind === '化退神') scoreWeightIds.push('transform-retreat');
    const scoreWeights = scoreWeightIds.map((id) => ({ id, value: weightValue(id) }));
    const score = scoreWeights.reduce((sum, { value }) => sum + value, 0);

    return {
      ...y,
      value: values[i],
      god: gods[i],
      state,
      changShengStage,
      moving: isMoving,
      void: isVoid,
      /** marks 的结构化镜像；旺衰“囚”仍只通过 state 表达。 */
      isVoid,
      isMonthBreak: monthClash,
      monthBreakSeverity,
      isDayBreak: marks.includes('日破'),
      // 中性事实：暗动/日破都是它的下游分类，彼此不互斥。
      isStaticClashedByDay: !isMoving && dayClash,
      isTomb: marks.includes('日墓'),
      isGrave: marks.includes('日绝'),
      isPunished: dayPunish || monthPunish,
      isFullPunish: Boolean(fullPunish),
      fullPunishGroup: fullPunish?.name || null,
      // 三刑全见时三组两支关系均成立；否则只统计爻与日/月直接相刑。
      punishPairCount: fullPunish ? 3 : Number(dayPunish) + Number(monthPunish),
      // 实际在卦上的爻不会“缺席”；伏藏用神会在 useGod/伏神上标 true。
      isAbsent: false,
      marks,
      transform,
      scoreWeights,
      score: Math.round(score * 100) / 100
    };
  });

  // 合起/合绊只描述爻被日、月或其他爻所合的当前事实。分吉凶需看被合
  // 对象，故本层不加权。爻与爻的六合独立检测，不能依赖 interactions
  // 的 else-if 顺序（同一对爻可能同时存在五行生克关系）。
  const lineCombinations = [];
  for (let i = 0; i < yao.length; i += 1) {
    for (let j = i + 1; j < yao.length; j += 1) {
      const first = yao[i];
      const second = yao[j];
      if (!combines(first.branch, second.branch)) continue;
      lineCombinations.push({
        first: { ...evidenceOf(first), moving: first.moving, isShi: first.isShi, isYing: first.isYing },
        second: { ...evidenceOf(second), moving: second.moving, isShi: second.isShi, isYing: second.isYing },
        combineState: first.moving || second.moving ? '合绊' : '合起',
        kind: '爻间作合'
      });
    }
  }
  for (const line of yao) {
    if (!line.transform || !combines(line.branch, line.transform.toBranch)) continue;
    lineCombinations.push({
      first: { ...evidenceOf(line), moving: line.moving, isShi: line.isShi, isYing: line.isYing },
      second: {
        relative: line.transform.toRelative,
        branch: line.transform.toBranch,
        position: line.position,
        ganzhi: line.transform.to,
        element: line.transform.toElement,
        moving: false,
        isShi: false,
        isYing: false,
        isTransformed: true
      },
      combineState: '合绊',
      kind: '动变作合'
    });
  }
  for (const line of yao) {
    const linePartners = yao.filter((actor) => actor.position !== line.position
      && combines(actor.branch, line.branch));
    const combinedBy = uniqueActors([
      ...(combines(line.branch, dayBranch)
        ? [{ source: '日辰', branch: dayBranch, ganzhi: pillars.day.name }]
        : []),
      ...(combines(line.branch, monthBranch)
        ? [{ source: '月建', branch: monthBranch, ganzhi: pillars.month.name }]
        : []),
      ...linePartners.map(actorEvidence)
    ]);
    line.combinedBy = combinedBy;
    line.combineState = combinedBy.length ? (line.moving ? '合绊' : '合起') : null;
    if (line.combineState && !line.marks.includes(line.combineState)) {
      line.marks.push(line.combineState);
    }
  }

  const calendarSupport = yao.flatMap((line) => [
    ...calendarEffects(line, '日辰', dayBranch),
    ...calendarEffects(line, '月建', monthBranch)
  ]);

  // ---- 动爻对其他爻的生克 ----
  const interactions = [];
  for (const src of yao.filter((y) => y.moving || y.marks.includes('暗动'))) {
    for (const dst of yao) {
      if (dst.position === src.position) continue;
      if (generates(src.element, dst.element)) {
        interactions.push({ from: src.position, to: dst.position, type: '生', activity: src.moving ? '明动' : '暗动', note: `${src.relative}${src.branch}动生${dst.relative}${dst.branch}` });
      } else if (overcomes(src.element, dst.element)) {
        interactions.push({ from: src.position, to: dst.position, type: '克', activity: src.moving ? '明动' : '暗动', note: `${src.relative}${src.branch}动克${dst.relative}${dst.branch}` });
      } else if (clashes(src.branch, dst.branch)) {
        interactions.push({ from: src.position, to: dst.position, type: '冲', activity: src.moving ? '明动' : '暗动', note: `${src.branch}动冲${dst.branch}` });
      } else if (combines(src.branch, dst.branch)) {
        interactions.push({ from: src.position, to: dst.position, type: '合', activity: src.moving ? '明动' : '暗动', note: `${src.branch}动合${dst.branch}` });
      }
    }
  }

  // ---- 卦象层面 ----
  const patterns = [];
  const primarySixClash = isSixClashHexagram(primary);
  const primarySixCombine = isSixCombineHexagram(primary);
  const changedSixClash = changed ? isSixClashHexagram(changed) : false;
  const changedSixCombine = changed ? isSixCombineHexagram(changed) : false;
  if (primarySixClash) patterns.push('本卦六冲');
  if (primarySixCombine) patterns.push('本卦六合');
  if (changedSixClash) patterns.push('变卦六冲');
  if (changedSixCombine) patterns.push('变卦六合');
  if (primarySixClash && changedSixCombine) patterns.push('六冲变六合');
  if (primarySixCombine && changedSixCombine) patterns.push('六合变六合');
  if (!hasChange) patterns.push('六爻安静');
  if (moving.length >= 4) patterns.push('乱动');
  // 三合局（需含动爻）
  for (const tc of TRIPLE_COMBINE) {
    const hit = yao.filter((y) => tc.branches.includes(y.branch));
    const uniq = new Set(hit.map((y) => y.branch));
    if (uniq.size === 3 && hit.some((y) => y.moving)) patterns.push(`三合${tc.element}局`);
  }
  // 伏吟 / 反吟（卦层面）
  if (changed) {
    if (changed.lower === primary.lower || changed.upper === primary.upper) {
      const same = yao.some((y) => y.transform && y.transform.kind === '伏吟');
      if (same) patterns.push('爻伏吟');
    }
    if (yao.some((y) => y.transform && y.transform.kind === '反吟')) patterns.push('爻反吟');
  }
  const tripleCombineEdges = tripleCombineRelations(patterns);
  const patternFlags = {
    primary: {
      isSixClash: primarySixClash,
      isSixCombine: primarySixCombine
    },
    changed: changed ? {
      isSixClash: changedSixClash,
      isSixCombine: changedSixCombine
    } : null,
    hasSixClash: primarySixClash || changedSixClash,
    hasSixCombine: primarySixCombine || changedSixCombine,
    isSixClashToSixCombine: primarySixClash && changedSixCombine,
    isSixCombineToSixCombine: primarySixCombine && changedSixCombine
  };

  // ---- 用神 ----
  // 未指定问事类别时不猜测六亲，按通用的“问自身/运势”取世爻。
  // 未知的非空类别仍保持未解析，避免拼写错误被静默当成自身之占。
  const resolved = useRelative
    || (category == null || category === '' ? '世爻' : USE_GOD_MAP[category] || null);
  let useGod = null;
  if (resolved) {
    if (resolved === '世爻') {
      const shiYao = yao.find((y) => y.isShi);
      useGod = {
        用神: '世爻',
        主爻: [shiYao],
        原神: null,
        忌神: null,
        仇神: null,
        伏藏: false
      };
    } else {
      const chain = relativeChain(resolved, primary.palaceElement);
      const pick = (rel) => yao.filter((y) => y.relative === rel);
      const main = pick(resolved);
      const hiddenUse = main.length === 0
        ? hidden.find((h) => h.relative === resolved) || null
        : null;
      useGod = {
        ...chain,
        主爻: main,
        原神爻: pick(chain.原神),
        忌神爻: pick(chain.忌神),
        仇神爻: pick(chain.仇神),
        伏藏: main.length === 0,
        伏神: hiddenUse ? { ...hiddenUse, isAbsent: true } : null
      };
    }
  }
  if (useGod) {
    const positions = new Set((useGod.主爻 || []).map(({ position }) => position));
    const incomingSources = (type) => [...new Set(
      interactions
        .filter((interaction) => interaction.type === type && positions.has(interaction.to))
        .map((interaction) => interaction.from)
    )].map((position) => yao[position - 1]);
    useGod.generatedBy = incomingSources('生');
    useGod.overcomeBy = incomingSources('克');
    useGod.isAbsent = useGod.伏藏;
    const primary = useGod.伏藏 ? useGod.伏神 : primaryUseLine(useGod.主爻 || []);
    useGod.primaryEvidence = evidenceOf(primary);

    // 只从既有五行生克、六冲、六合表反查地支，不在这里判断何时应验。
    const useLines = useGod.主爻?.length ? useGod.主爻 : useGod.伏神 ? [useGod.伏神] : [];
    const useElement = useLines[0]?.element;
    useGod.reviveBranches = useElement
      ? BRANCHES.filter((branch) => generates(BRANCH_ELEMENT[branch], useElement))
      : [];
    useGod.clashBranches = [...new Set(
      useLines.flatMap(({ branch }) => BRANCHES.filter((candidate) => clashes(candidate, branch)))
    )];
    useGod.combineBranches = [...new Set(
      useLines.map(({ branch }) => SIX_COMBINE[branch]).filter(Boolean)
    )];
  }

  const combinedFacts = compoundFacts({ yao, interactions, calendarSupport, pillars, useGod });
  const fiveElements = ['木', '火', '土', '金', '水'];
  const presentElements = [...new Set(yao.map(({ element }) => element))];
  const elementPresence = {
    present: fiveElements.filter((element) => presentElements.includes(element)),
    missing: fiveElements.filter((element) => !presentElements.includes(element))
  };

  return {
    question,
    category: category || null,
    timeHorizon,
    timeScope,
    pillars: {
      at: pillars.at,
      calendarOptions: pillars.calendarOptions,
      年: pillars.year.name,
      月: pillars.month.name,
      日: pillars.day.name,
      时: pillars.hour.name,
      节: pillars.term,
      旬空: voids
    },
    primary,
    changed,
    moving,
    hidden,
    gods,
    yao,
    calendarSupport,
    interactions,
    lineCombinations,
    elementPresence,
    compoundFacts: combinedFacts,
    patterns,
    tripleCombineRelations: tripleCombineEdges,
    patternFlags,
    useGod,
    /** 给上层（含 LLM）用的纯事实清单 */
    facts: buildFacts({ primary, changed, yao, moving, patterns, useGod, pillars })
  };
}

function buildFacts({ primary, changed, yao, moving, patterns, useGod, pillars }) {
  const f = [];
  f.push(`本卦 ${primary.name}（${primary.palace}宫${primary.rankLabel}），世在${primary.shi}爻，应在${primary.ying}爻`);
  if (changed) f.push(`变卦 ${changed.name}（${changed.palace}宫${changed.rankLabel}）`);
  f.push(`月建${pillars.month.branch}，日辰${pillars.day.name}，旬空${pillars.voidBranches.join('')}`);
  f.push(moving.length ? `动爻：${moving.join('、')}爻` : '六爻安静，无动爻');
  for (const y of yao) {
    const bits = [
      `${y.position}爻`,
      y.god,
      `${y.relative}${y.ganzhi}${y.element}`,
      y.isShi ? '【世】' : y.isYing ? '【应】' : '',
      y.moving ? '动' : '',
      y.state,
      y.marks.length ? `(${y.marks.join('、')})` : '',
      y.transform ? `→${y.transform.toRelative}${y.transform.to}` : ''
    ].filter(Boolean);
    f.push(bits.join(' '));
  }
  if (patterns.length) f.push(`卦象：${patterns.join('、')}`);
  if (useGod && useGod.用神 !== '世爻') {
    f.push(
      `用神取${useGod.用神}（${useGod.用神五行}），原神${useGod.原神}，忌神${useGod.忌神}，仇神${useGod.仇神}` +
        (useGod.伏藏 ? `；用神不上卦，伏于${useGod.伏神 ? useGod.伏神.position + '爻' + useGod.伏神.flying.relative + '之下' : '卦中'}` : '')
    );
  }
  return f;
}
